import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  ChartBarIcon,
  FireIcon,
  PlusIcon,
  ScaleIcon,
  TrashIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline'
import { trackRecordAPI } from '../../services/api'
import type { PerformancePeriod, TradeRecord, TrackRecordSummary } from '../../types'
import ConfirmDialog from '../ConfirmDialog'
import Spinner from '../Spinner'
import Tooltip from '../Tooltip'
import { PROFILE_GLOSSARY, TRACK_RECORD_GLOSSARY } from '../../config/glossary'
import LogTradeModal from './LogTradeModal'
import CloseTradeModal from './CloseTradeModal'
import { getStreakColor, getStreakLabel } from './streakDisplay'
import { formatWinRate, hasWinRate, pendingWinRateNote } from '../../utils/winRate'
import { formatPnlPct, pnlColorClass } from '../../utils/pnl'
import { formatTradePrice } from '../../utils/marketPrice'

const PERIOD_LABELS: Record<PerformancePeriod, string> = {
  all_time: 'All Time',
  year: 'This Year',
  quarter: 'This Quarter',
  month: 'This Month',
  week: 'This Week',
}

const OUTCOME_STYLES: Record<TradeRecord['outcome'], string> = {
  win: 'bg-success-50 text-success-700',
  loss: 'bg-danger-50 text-danger-700',
  breakeven: 'bg-gray-100 text-gray-700',
  open: 'bg-primary-50 text-primary-700',
}

const formatDateTime = (value: string) =>
  new Date(value).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

const TrackRecordSection = () => {
  const [summary, setSummary] = useState<TrackRecordSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState<PerformancePeriod>('all_time')
  const [showLogModal, setShowLogModal] = useState(false)
  const [closingTrade, setClosingTrade] = useState<TradeRecord | null>(null)
  const [deletingTrade, setDeletingTrade] = useState<TradeRecord | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    loadSummary(period)
  }, [period])

  const loadSummary = async (forPeriod: PerformancePeriod) => {
    setLoading(true)
    try {
      const data = await trackRecordAPI.getSummary(forPeriod)
      setSummary(data)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load your journal')
    } finally {
      setLoading(false)
    }
  }

  const handleTradeCreated = () => {
    loadSummary(period)
  }

  const handleTradeClosed = () => {
    setClosingTrade(null)
    loadSummary(period)
  }

  const handleDeleteTrade = async () => {
    if (!deletingTrade) return

    setDeleting(true)
    try {
      await trackRecordAPI.deleteTrade(deletingTrade.id)
      toast.success('Trade deleted')
      setDeletingTrade(null)
      loadSummary(period)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete trade')
    } finally {
      setDeleting(false)
    }
  }

  const stats = summary?.stats
  const trades = summary?.recent_trades ?? []

  const streakLabel = getStreakLabel(stats)
  const streakColor = getStreakColor(stats)

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Personal journal (self-reported)</h3>
          {/* The automated record is /track-record; this must never read as it. */}
          <p className="text-xs text-gray-500">
            Trades you log yourself. Not verified, and never counted in the Track Record.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            aria-label="Period"
            value={period}
            onChange={(e) => setPeriod(e.target.value as PerformancePeriod)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {(Object.keys(PERIOD_LABELS) as PerformancePeriod[]).map((p) => (
              <option key={p} value={p}>
                {PERIOD_LABELS[p]}
              </option>
            ))}
          </select>
          <Tooltip content={PROFILE_GLOSSARY.periodFilter} label="the period filter" />
          <button
            onClick={() => setShowLogModal(true)}
            className="flex items-center gap-1 px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
          >
            <PlusIcon className="h-4 w-4" />
            Log Trade
          </button>
          <Tooltip content={TRACK_RECORD_GLOSSARY.logTrade} label="Log Trade" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-center mb-1">
                <TrophyIcon className="h-5 w-5 text-primary-500" />
              </div>
              <p
                className={`text-xl font-bold ${
                  !hasWinRate(stats?.win_rate_pct, stats?.total_trades ?? 0)
                    ? 'text-gray-400'
                    : (stats?.win_rate_pct ?? 0) >= 50
                    ? 'text-success-600'
                    : 'text-gray-900'
                }`}
              >
                {formatWinRate(stats?.win_rate_pct, stats?.total_trades ?? 0)}
              </p>
              <p className="text-xs text-gray-500">Win Rate</p>
              {!hasWinRate(stats?.win_rate_pct, stats?.total_trades ?? 0) && (
                <p className="text-xs text-gray-400">{pendingWinRateNote(stats?.total_trades, 'trade')}</p>
              )}
            </div>

            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-center mb-1">
                <ChartBarIcon className="h-5 w-5 text-primary-500" />
              </div>
              <p className="text-xl font-bold text-gray-900">{stats?.total_trades ?? 0}</p>
              <p className="text-xs text-gray-500">
                <Tooltip content={PROFILE_GLOSSARY.winLossCount}>
                  {stats?.winning_trades ?? 0}W / {stats?.losing_trades ?? 0}L
                </Tooltip>
              </p>
            </div>

            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-center mb-1">
                <ChartBarIcon className="h-5 w-5 text-primary-500" />
              </div>
              <p
                className={`text-xl font-bold ${
                  (stats?.total_roi_pct ?? 0) >= 0 ? 'text-success-600' : 'text-danger-600'
                }`}
              >
                {(stats?.total_roi_pct ?? 0) >= 0 ? '+' : ''}
                {(stats?.total_roi_pct ?? 0).toFixed(2)}%
              </p>
              <p className="text-xs text-gray-500">
                <Tooltip content={PROFILE_GLOSSARY.totalRoi}>Sum of trade returns</Tooltip>
              </p>
              <p className="text-[10px] leading-tight text-gray-400">unsized, not account return</p>
            </div>

            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-center mb-1">
                <ScaleIcon className="h-5 w-5 text-primary-500" />
              </div>
              <p className="text-xl font-bold text-gray-900">
                {(stats?.average_rr_ratio ?? 0).toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">
                <Tooltip content={TRACK_RECORD_GLOSSARY.avgRR}>Avg R:R</Tooltip>
              </p>
            </div>

            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-center mb-1">
                <FireIcon className="h-5 w-5 text-primary-500" />
              </div>
              <p className={`text-xl font-bold ${streakColor}`}>{streakLabel}</p>
              <p className="text-xs text-gray-500">
                <Tooltip content={PROFILE_GLOSSARY.currentStreak}>Current Streak</Tooltip>
              </p>
            </div>
          </div>

          {trades.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">
              No trades logged yet. Log a trade you took to start your journal.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {trades.map((trade) => (
                <li key={trade.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`px-2 py-0.5 text-xs font-semibold rounded ${
                        trade.direction === 'LONG'
                          ? 'bg-success-50 text-success-700'
                          : 'bg-danger-50 text-danger-700'
                      }`}
                    >
                      {trade.direction}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{trade.symbol}</p>
                      <p className="text-xs text-gray-500">
                        {formatDateTime(trade.entry_time)} &middot; Entry {formatTradePrice(trade.entry_price)}
                        {trade.exit_price != null && ` → Exit ${formatTradePrice(trade.exit_price)}`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {trade.profit_loss_pct != null && (
                      <span className={`text-sm font-semibold ${pnlColorClass(trade.profit_loss_pct)}`}>
                        {formatPnlPct(trade.profit_loss_pct)}
                      </span>
                    )}
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded ${OUTCOME_STYLES[trade.outcome]}`}
                    >
                      {trade.outcome}
                    </span>
                    {trade.outcome === 'open' && (
                      <button
                        onClick={() => setClosingTrade(trade)}
                        className="px-2 py-1 text-xs font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                      >
                        Close
                      </button>
                    )}
                    <button
                      onClick={() => setDeletingTrade(trade)}
                      className="p-1 text-gray-400 hover:text-danger-600"
                      aria-label={`Delete trade ${trade.symbol}`}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <LogTradeModal
        isOpen={showLogModal}
        onClose={() => setShowLogModal(false)}
        onCreated={handleTradeCreated}
      />

      {closingTrade && (
        <CloseTradeModal
          trade={closingTrade}
          onClose={() => setClosingTrade(null)}
          onClosed={handleTradeClosed}
        />
      )}

      <ConfirmDialog
        isOpen={deletingTrade !== null}
        title="Delete trade"
        message={`Are you sure you want to delete the ${deletingTrade?.symbol} trade? This action cannot be undone.`}
        confirmLabel="Delete"
        confirming={deleting}
        danger
        onConfirm={handleDeleteTrade}
        onCancel={() => setDeletingTrade(null)}
      />
    </div>
  )
}

export default TrackRecordSection
